import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mcpConnectionsApi } from '@/services/api';
import type { AssistantAvailableConnection } from '@/types/assistant';
import type { CreateMcpConnectionInput, UpdateMcpConnectionInput, McpConnectionFilters, McpToolsResponse } from '@/types';

export const useMcpConnections = (page = 1, limit = 20, filters?: McpConnectionFilters) => {
  return useQuery({
    queryKey: ['mcp-connections', page, limit, filters],
    queryFn: () => mcpConnectionsApi.getAll(page, limit, filters).then(res => res.data),
  });
};

export const useMcpConnection = (id: string | undefined) => {
  return useQuery({
    queryKey: ['mcp-connection', id],
    queryFn: () => mcpConnectionsApi.getById(id!).then(res => res.data.data),
    enabled: !!id,
  });
};

export const useCreateMcpConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMcpConnectionInput) =>
      mcpConnectionsApi.create(data).then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-connections'] });
    },
  });
};

export const useUpdateMcpConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMcpConnectionInput }) =>
      mcpConnectionsApi.update(id, data).then(res => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['mcp-connections'] });
      queryClient.invalidateQueries({ queryKey: ['mcp-connection', variables.id] });
    },
  });
};

export const useDeleteMcpConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mcpConnectionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-connections'] });
    },
  });
};

export const useMcpConnectionTools = (id: string | undefined, enabled: boolean = false) => {
  return useQuery<McpToolsResponse>({
    queryKey: ['mcp-connection-tools', id],
    queryFn: () => mcpConnectionsApi.listTools(id!).then(res => res.data.data),
    enabled: enabled && !!id,
    staleTime: 30000, // 30 seconds
    retry: false, // Don't retry on failure - user should manually retry
  });
};

/**
 * The account's ACTIVE connections reduced to what the Assistant may see
 * (task 961): name and description, nothing else.
 *
 * The Composer guide holds no tools, so this list is the only way it learns
 * what the account has connected. The mapping here is the guard that keeps the
 * rest — `url`, `authType`, `hasCredential` — off the wire: the turn cannot
 * carry a field this function does not build.
 *
 * Failure is not an error state for the caller. A refused or failed list simply
 * yields none, and the guide falls back to its seeded pair, which is exactly the
 * behaviour every caller had before this existed.
 */
export const useActiveMcpConnectionSummaries = () => {
  const query = useQuery({
    queryKey: ['mcp-connections', 1, 100, { isActive: true }],
    queryFn: () => mcpConnectionsApi.getAll(1, 100, { isActive: true }).then((res) => res.data),
    // The sidebar asks on every turn; the list changes when someone visits the
    // MCP Connections page, not between messages.
    staleTime: 5 * 60_000,
    retry: false,
  });

  const connections: AssistantAvailableConnection[] = (query.data?.data ?? []).map((connection) => ({
    name: connection.name,
    ...(connection.description ? { description: connection.description } : {}),
  }));

  return { connections, isResolved: query.isSuccess };
};
